import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { buildApiUrl } from '../config/api';
import { AnalysisFeedback } from './AnalysisFeedback';

interface AnalysisResponse {
  main: string;
  strategic: string;
  tactical: string;
  critical: string;
}

export const AIGoalAnalyzer: React.FC = () => {
  const { token } = useAuth();
  const [goalInput, setGoalInput] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null);
  const [showThinking, setShowThinking] = useState(true);
  const [activeTab, setActiveTab] = useState<'main' | 'strategic' | 'tactical' | 'critical'>('main');
  const [actionOnlyMode, setActionOnlyMode] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  const handleAnalyze = async () => {
    if (!goalInput.trim() || !token) return;

    setIsAnalyzing(true);
    setAnalysis(null);

    try {
      const response = await fetch(buildApiUrl('/ai/analyze'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt: goalInput }),
      });

      if (response.ok) {
        const data = await response.json();
        setAnalysis(data.analysis);
        setCurrentSessionId(data.sessionId || null);
      } else {
        console.error('Analysis failed');
      }
    } catch (error) {
      console.error('Error:', error);
    }

    setIsAnalyzing(false);
  };

  const formatAnalysis = (text: string) => {
    if (!text) return null;

    // Check if this section contains "DO THIS NEXT"
    const hasDoThisNext = text.includes('DO THIS NEXT');

    // Split by sections and format
    const sections = text.split(/\n(?=[A-Z🎯\s]+\n[-=]+)|\n(?=🎯)/);

    return sections.map((section, idx) => {
      const lines = section.trim().split('\n');
      const title = lines[0];
      const content = lines.slice(1).join('\n');

      // Special formatting for "DO THIS NEXT" section
      const isDoThisNext = title?.includes('DO THIS NEXT');

      if (isDoThisNext) {
        return (
          <div key={idx} className="mb-4 p-4 bg-gradient-to-r from-orange-500 to-teal-500 rounded-lg text-white">
            <h4 className="font-bold text-xl mb-3 flex items-center">
              <span className="text-2xl mr-2">🎯</span>
              DO THIS NEXT (RIGHT NOW)
            </h4>
            <div className="text-lg font-medium bg-white/20 backdrop-blur rounded p-3">
              {content.replace(/[-=]+/g, '').trim()}
            </div>
            <button
              onClick={() => {
                // Copy action to clipboard
                navigator.clipboard.writeText(content.replace(/[-=]+/g, '').trim());
                alert('Action copied to clipboard! Now go do it! 🚀');
              }}
              className="mt-3 px-4 py-2 bg-white/30 hover:bg-white/40 rounded-lg font-bold transition-all"
            >
              Copy Action & GO! →
            </button>
          </div>
        );
      }

      return (
        <div key={idx} className="mb-4">
          {title && (
            <h4 className="font-bold text-gray-900 mb-2 border-b border-orange-200 pb-1">
              {title.replace(/[-=]+/g, '').trim()}
            </h4>
          )}
          <div className="text-gray-900 whitespace-pre-wrap font-mono text-sm">
            {content.replace(/[-=]+/g, '').trim()}
          </div>
        </div>
      );
    });
  };

  const tabs = [
    { id: 'main', label: '📊 Goal Analysis', color: 'orange' },
    { id: 'strategic', label: '🎯 Strategic', color: 'blue' },
    { id: 'tactical', label: '⚡ Tactical', color: 'green' },
    { id: 'critical', label: '🔍 Critical', color: 'red' },
  ];

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-xl shadow-lg border border-orange-100 p-6">
        <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-orange-500 to-teal-500 bg-clip-text text-transparent">
          🎯 Multi-Model Goal Analyzer
        </h2>

        {/* Input Section */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2 text-gray-700">
            Enter your goal for analysis:
          </label>
          <textarea
            value={goalInput}
            onChange={(e) => setGoalInput(e.target.value)}
            placeholder="e.g., I want to create a feature that understands goal input..."
            className="w-full p-4 rounded-lg bg-white text-gray-800 placeholder-gray-400
                     border-2 border-orange-300 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-200
                     resize-none h-32 font-sans"
            maxLength={2000}
          />
          <div className="flex justify-between items-center mt-2">
            <span className="text-xs text-gray-500">{goalInput.length}/2000 characters</span>
            <div className="flex items-center space-x-4">
              <label className="flex items-center text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={actionOnlyMode}
                  onChange={(e) => setActionOnlyMode(e.target.checked)}
                  className="mr-2 rounded text-orange-500"
                />
                <span className="font-bold text-orange-600">Just tell me what to do!</span>
              </label>
              <label className="flex items-center text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={showThinking}
                  onChange={(e) => setShowThinking(e.target.checked)}
                  className="mr-2 rounded text-teal-500"
                />
                Show thinking
              </label>
            </div>
          </div>
        </div>

        {/* Analyze Button */}
        <button
          onClick={handleAnalyze}
          disabled={!goalInput.trim() || isAnalyzing || !token}
          className={`w-full px-8 py-4 rounded-lg font-bold text-lg text-white transition-all transform
            ${!goalInput.trim() || isAnalyzing || !token
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-orange-500 to-teal-500 hover:from-orange-600 hover:to-teal-600 hover:shadow-lg'
            }`}
        >
          {isAnalyzing ? (
            <span className="flex items-center justify-center">
              <span className="animate-spin mr-2">⚡</span>
              Analyzing with Multiple Models...
            </span>
          ) : (
            '🚀 Analyze Goal with AI'
          )}
        </button>

        {/* Thinking Process (if enabled) */}
        {showThinking && isAnalyzing && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="font-bold text-gray-700 mb-2">🧠 AI Thinking Process:</h3>
            <div className="space-y-2 text-sm text-gray-600 font-mono">
              <div className="animate-pulse">→ Detecting language...</div>
              <div className="animate-pulse delay-100">→ Classifying input type...</div>
              <div className="animate-pulse delay-200">→ Checking for anomalies...</div>
              <div className="animate-pulse delay-300">→ Extracting core intent...</div>
              <div className="animate-pulse delay-400">→ Running strategic analysis...</div>
              <div className="animate-pulse delay-500">→ Running tactical breakdown...</div>
              <div className="animate-pulse delay-600">→ Running critical evaluation...</div>
              <div className="animate-pulse delay-700">→ Synthesizing recommendations...</div>
            </div>
          </div>
        )}

        {/* Action Only Mode - Big, Bold, Single Action */}
        {actionOnlyMode && analysis && (
          <div className="mt-6 p-8 bg-gradient-to-r from-orange-500 to-teal-500 rounded-xl text-white">
            <h2 className="text-3xl font-bold mb-4 flex items-center">
              <span className="text-4xl mr-3">🎯</span>
              DO THIS RIGHT NOW
            </h2>
            <div className="text-2xl font-medium bg-white/20 backdrop-blur rounded-lg p-6 mb-4">
              {(() => {
                const mainText = analysis.main || '';
                const match = mainText.match(/DO THIS NEXT[^]*?\n([^\n]+(?:\n[^\n]+)*?)(?=\n\n|\n[A-Z]|$)/);
                return match ? match[1].trim() : "Open your code editor and create a new file called 'next-action.txt'";
              })()}
            </div>
            <button
              onClick={() => {
                const mainText = analysis.main || '';
                const match = mainText.match(/DO THIS NEXT[^]*?\n([^\n]+(?:\n[^\n]+)*?)(?=\n\n|\n[A-Z]|$)/);
                const action = match ? match[1].trim() : "Open your code editor";
                navigator.clipboard.writeText(action);
                alert('Action copied! Close this and GO DO IT! 🚀');
              }}
              className="px-8 py-4 bg-white text-orange-600 rounded-lg font-bold text-xl hover:scale-105 transition-transform"
            >
              Copy & Start Now →
            </button>
            <div className="mt-4 text-sm opacity-80">
              Turn off "Just tell me what to do!" to see full analysis
            </div>
          </div>
        )}

        {/* Results Tabs - Hide in action-only mode */}
        {analysis && !actionOnlyMode && (
          <div className="mt-6">
            <div className="flex space-x-2 mb-4 border-b border-gray-200">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-4 py-2 font-medium transition-all
                    ${activeTab === tab.id
                      ? `border-b-2 border-${tab.color}-500 text-${tab.color}-600`
                      : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Analysis Content */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 min-h-[400px]">
              {activeTab === 'main' && (
                <div className="space-y-4">
                  {formatAnalysis(analysis.main) || (
                    <pre className="whitespace-pre-wrap font-mono text-sm text-gray-900">
                      {analysis.main}
                    </pre>
                  )}
                </div>
              )}

              {activeTab === 'strategic' && (
                <div>
                  <h3 className="font-bold text-blue-700 mb-3">Strategic Analysis (Mistral 7B)</h3>
                  <pre className="whitespace-pre-wrap font-mono text-sm text-gray-900">
                    {analysis.strategic}
                  </pre>
                </div>
              )}

              {activeTab === 'tactical' && (
                <div>
                  <h3 className="font-bold text-green-700 mb-3">Tactical Breakdown (Llama 3)</h3>
                  <pre className="whitespace-pre-wrap font-mono text-sm text-gray-900">
                    {analysis.tactical}
                  </pre>
                </div>
              )}

              {activeTab === 'critical' && (
                <div>
                  <h3 className="font-bold text-red-700 mb-3">Critical Evaluation (Mistral 7B)</h3>
                  <pre className="whitespace-pre-wrap font-mono text-sm text-gray-900">
                    {analysis.critical}
                  </pre>
                </div>
              )}
            </div>

            {/* Model Comparison Summary */}
            <div className="mt-4 p-4 bg-teal-50 rounded-lg border border-teal-200">
              <h4 className="font-bold text-teal-700 mb-2">💡 Multi-Model Insights:</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Models Used:</span>
                  <ul className="mt-1 text-gray-600">
                    <li>• Llama 3 8B (Main & Tactical)</li>
                    <li>• Mistral 7B (Strategic & Critical)</li>
                  </ul>
                </div>
                <div>
                  <span className="font-medium">Analysis Perspectives:</span>
                  <ul className="mt-1 text-gray-600">
                    <li>• Structured goal breakdown</li>
                    <li>• Long-term strategic view</li>
                    <li>• Immediate action items</li>
                    <li>• Risk & gap analysis</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Feedback Component - Shows after analysis */}
        {analysis && currentSessionId && (
          <AnalysisFeedback
            sessionId={currentSessionId}
            onFeedbackSubmitted={() => {
              console.log('Feedback submitted for session:', currentSessionId);
            }}
          />
        )}
      </div>
    </div>
  );
};